/**
 * The person who owns a record.
 *
 * Initials rather than a photograph: there are no avatars in the system, and a
 * generated colour block with initials is legible at the sizes a table row
 * allows.
 *
 * The name is always available to assistive technology. Initials alone are
 * ambiguous — "RS" identifies nobody without the full name behind it.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.16, PRD §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { initialsOf } from './initials';

export interface OwnerAvatarProps {
  /** Null when the record has no owner. */
  name: string | null | undefined;
  className?: string;
}

export function OwnerAvatar({ name, className }: OwnerAvatarProps): React.JSX.Element {
  const { t } = useTranslation();

  const initials = name === null || name === undefined ? '' : initialsOf(name);
  const unassigned = initials === '';

  return (
    <span
      className={cn(
        'font-display inline-grid size-6 flex-none place-items-center rounded-full',
        'text-[10px] font-extrabold',
        unassigned ? 'bg-gray-100 text-gray-400' : 'bg-blue-light text-blue-strong',
        className,
      )}
      role="img"
      aria-label={
        unassigned ? t('startup.unassigned') : t('startup.ownerNamed', { name })
      }
    >
      <span aria-hidden="true">{unassigned ? '?' : initials}</span>
    </span>
  );
}
